import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import Gallery from "./Gallery";
import { loadSetting, saveSettingsSlideshow } from "../utils/Network";

function createSlideInternalId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sld_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

const normalizeSlide = (item) => {
  if (typeof item === "string") {
    const image = item.trim();
    return image ? { image, url: "", _sortId: createSlideInternalId() } : null;
  }
  if (!item || typeof item !== "object") return null;
  const image = typeof item.image === "string" ? item.image.trim() : "";
  const url = typeof item.url === "string" ? item.url.trim() : "";
  if (!image) return null;
  const _sortId =
    typeof item._sortId === "string" && item._sortId
      ? item._sortId
      : createSlideInternalId();
  return { image, url, _sortId };
};

const parseSlides = (value) => {
  if (!value) return [];
  const source = typeof value === "string" ? (() => {
    try {
      return JSON.parse(value);
    } catch (error) {
      return [];
    }
  })() : value;
  if (!Array.isArray(source)) return [];
  return source.map(normalizeSlide).filter(Boolean);
};

const slidesForPayload = (list) =>
  list.map(({ image, url }) => ({ image, url: url || "" }));

export default function SettingsSlideshowContent() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [settingsId, setSettingsId] = useState(null);
  const [slides, setSlides] = useState([]);
  const [galleryModalIsOpen, setGalleryModalIsOpen] = useState(false);
  const [gallerySlideIndex, setGallerySlideIndex] = useState(null);

  useEffect(() => {
    setLoading(true);
    loadSetting()
      .then((setting) => {
        if (!setting) return;
        setSettingsId(setting.id || null);
        setSlides(parseSlides(setting.slideshow_images));
      })
      .finally(() => setLoading(false));
  }, []);

  const openGalleryAdd = () => {
    setGallerySlideIndex(null);
    setGalleryModalIsOpen(true);
  };

  const openGalleryEditSlide = (index) => {
    setGallerySlideIndex(index);
    setGalleryModalIsOpen(true);
  };

  const onGallerySelect = (galleryItem) => {
    const image = galleryItem?.image;
    if (!image) return;
    if (gallerySlideIndex === null) {
      setSlides((prev) => {
        if (prev.some((slide) => slide.image === image)) return prev;
        return [...prev, { image, url: "", _sortId: createSlideInternalId() }];
      });
    } else {
      const idx = gallerySlideIndex;
      setSlides((prev) =>
        prev.map((slide, i) => (i === idx ? { ...slide, image } : slide))
      );
    }
  };

  const updateSlideUrl = (index, url) => {
    setSlides((prev) =>
      prev.map((slide, i) => (i === index ? { ...slide, url } : slide))
    );
  };

  const removeSlide = (index) => {
    setSlides((prev) => prev.filter((_, i) => i !== index));
  };

  const onDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;
    setSlides((prev) => {
      const next = [...prev];
      const [removed] = next.splice(source.index, 1);
      next.splice(destination.index, 0, removed);
      return next;
    });
  };

  const saveChanges = () => {
    setError(null);
    setLoading(true);
    saveSettingsSlideshow(settingsId, JSON.stringify(slidesForPayload(slides)))
      .then((data) => {
        if (data?.id) {
          setSettingsId(data.id);
          setSlides(parseSlides(data.slideshow_images));
        }
      })
      .catch(() => {
        setError("Failed to save slideshow settings.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Gallery
        setSelectedGalleryItem={onGallerySelect}
        isOpen={galleryModalIsOpen}
        key={`slideshow-gallery-${galleryModalIsOpen}-${gallerySlideIndex ?? "add"}`}
        closeGallery={() => {
          setGalleryModalIsOpen(false);
          setGallerySlideIndex(null);
        }}
      />
      <div className="content-wrapper cms-ratings-surface">
        <section className="content-header">
          <h3 className="mb-3"> </h3>
          <ol className="breadcrumb">
            <li>
              <a href="/cms">
                <i className="fa fa-dashboard"></i> Home
              </a>
            </li>
            <li>
              <a href="/cms/settings">Setting</a>
            </li>
            <li className="active">Slideshow</li>
          </ol>
        </section>

        <section className="content padding-top-sm-0">
          <div className="row">
            <div className="col-md-12">
              <div className="box box-primary">
                <div className="box-header with-border">
                  <h3 className="box-title mt-5">
                    Slideshow{" "}
                    <span style={{ fontSize: 11 }}>({slides.length})</span>
                    {slides.length > 1 && (
                      <span
                        className="text-muted font-weight-normal"
                        style={{ fontSize: 12, marginLeft: "0.5em" }}
                      >
                        Drag the handle on each slide to reorder.
                      </span>
                    )}
                  </h3>
                  <button
                    type="button"
                    title="Add image from gallery"
                    className="btn btn-cm margin-r-5 pull-right mt-2p"
                    onClick={openGalleryAdd}
                  >
                    <i className="fa fa-plus"></i>
                  </button>
                </div>
                <div className="box-body p-4">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="slideshow-slides">
                      {(droppableProvided) => (
                        <div
                          className="row"
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {slides.map((slide, index) => (
                            <Draggable
                              key={slide._sortId}
                              draggableId={slide._sortId}
                              index={index}
                            >
                              {(provided) => (
                                <div
                                  className="col-md-3 mb-15"
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <div className="thumbnail">
                                    <div
                                      className="cms-slideshow-drag-handle text-center text-muted"
                                      {...provided.dragHandleProps}
                                      title="Drag to reorder"
                                      style={{
                                        cursor: "grab",
                                        padding: "6px 0",
                                        borderBottom: "1px solid #eee",
                                      }}
                                    >
                                      <i className="fa fa-arrows" aria-hidden />
                                    </div>
                          <button
                            type="button"
                            className="btn btn-link btn-block"
                            style={{
                              border: "none",
                              padding: 0,
                              boxShadow: "none",
                              width: "100%",
                              cursor: "pointer",
                            }}
                            onClick={() => openGalleryEditSlide(index)}
                            title="Change image from gallery"
                          >
                            <img
                              src={slide.image}
                              alt={`Slide ${index + 1}`}
                              className="img-responsive"
                            />
                          </button>
                          <div className="caption text-center">
                            <input
                              type="text"
                              className="form-control mb-8"
                              placeholder="Slide URL (optional)"
                              value={slide.url || ""}
                              onChange={(e) => updateSlideUrl(index, e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-cm btn-sm "
                              onClick={() => removeSlide(index)}
                            >
                              <i className="fa fa-trash margin-r-5"></i>
                            </button>
                          </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {!slides.length && (
                    <p className="text-muted mb-0">
                      No slideshow images yet. Use the + button in the header to add from the gallery.
                    </p>
                  )}
                </div>
                <div className="box-footer">
                  <button
                    type="button"
                    className="btn btn-cm pull-right"
                    onClick={saveChanges}
                  >
                    Save Changes
                  </button>
                  <a href="/cms/settings" className="btn btn-cm pull-left">
                    Back
                  </a>
                </div>
                {error && (
                  <div className="alert alert-danger mt-5 mb-0">{error}</div>
                )}
                {loading && (
                  <div className="alert mb-0">Please Wait...</div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
